import * as THREE from 'three';
import { LoadStl, cloneMeshWithTransform, clearDuplicateVertices, GetInsoleBox, flipFaceNormals, areNormalsInverted, extrudeAlongY } from 'Utils/ViewerUtils';
import { BoundedValue } from './BoundedValue';
import { AdditionImode, DEFAULT_ADDITON_MESH_MATERIAL, Hardness, HardnessValueMap, getAdditionRenderOrder } from '../components/Viewer3d/consts'
import { Brush, Evaluator, INTERSECTION } from 'three-bvh-csg';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js';
import type { DesignCategory } from './InsoleDesign';

const HEIGHT_MIN = 1;
const HEIGHT_MAX = 10;
const HEIGHT_MAX_FOREFOOT = 5;
const MIN_FLOOR_FOR_ADDITIONS = 1;

const FOREFOOT_ADDITION_NAMES = ['Subantero 2-5', 'Subantero 1-5', 'Whole Forefoot 0° at Toes'];
const ADDITION_TIES_TO_INSOLE_INDEX_NAMES: Readonly<Record<string, number>> = {
  'Subantero 1-5': 24,
  'Subantero 2-5': 24,
  'Pelotte 2': 203,
  'Pelotte 3': 203,
  'Pelotte 4': 203,
  'Pelotte 4 vlak': 203,
};
const ADDITION_TIES_TO_SANDAL_INDEX_NAMES: Readonly<Record<string, number>> = {
  'Subantero 1-5': 516,
  'Subantero 2-5': 516,
  'Pelotte 2': 578,
  'Pelotte 3': 578,
  'Pelotte 4': 578,
  'Pelotte 4 vlak': 578,
};

const ADDITION_TIES_TO_INDEX_NAMES_BY_CATEGORY: Readonly<
  Record<DesignCategory, Readonly<Record<string, number>>>
> = {
  Insole: ADDITION_TIES_TO_INSOLE_INDEX_NAMES,
  Sandal: ADDITION_TIES_TO_SANDAL_INDEX_NAMES,
  HighHeel: ADDITION_TIES_TO_INSOLE_INDEX_NAMES,
};

const DEFAULT_DESIGN_CATEGORY = 'Insole' as DesignCategory;

export enum AutomaticAdditions {
  None,
  MetatarsalBar,
  MetatarsalPad,
  MortonExtention,
  DancerPad,
  CutoutMet1,
  CutoutMet2,
  CutoutMet3,
  CutoutMet4,
  CutoutMet5,
  MetatarsalBarFromPescavus
};

export enum AdditionNewStatus {
  isNew,
  removed,
  none
}

export enum AdditionPaintType {
  ByConstant,
  ByHardness
}

export class Addition {
  mesh!: THREE.Mesh;
  viewMesh!: THREE.Mesh;
  cutMesh!: THREE.Mesh;
  // viewCurve!: LineSegments2;
  name: string = '';
  fileName: string = '';
  groupName: string = '';
  id: number = 0;
  designId: number = 0;
  designAdditionId: number = 0;
  color: number = 0x00aa00;
  height: BoundedValue = new BoundedValue(0, HEIGHT_MIN, HEIGHT_MAX);
  centerIndex: number = 0;
  meshPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  defaultRelativeLocation: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  relativeIndexMesh: number | null = null;
  status: AdditionNewStatus = AdditionNewStatus.none;
  isUpdated: boolean = false;
  scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);
  angle: number = 0;
  isReverse: boolean = false;
  boxOrigin: THREE.Box3 = new THREE.Box3();
  foot: 'right' | 'left';
  hardness: Hardness
  centerIndexOnViewerMesh: number = 0;
  jointGeometry: THREE.Mesh | null = null;
  boundariesIndex: number[] = [];

  debug_shape: THREE.Line | null = null;
  debugLines: THREE.Line[] = [];
  debugSpheres: THREE.Mesh[] = [];
  automaticAddition: AutomaticAdditions = AutomaticAdditions.None;

  mode: AdditionImode = AdditionImode.Bottom;
  thickness: number = 0;

  lastCutMesh: THREE.Mesh | null = null;
  groupNameAceess: number = 1;
  paintType: AdditionPaintType = AdditionPaintType.ByConstant;
  additionCenter: THREE.Vector3 = new THREE.Vector3(0, 0, 0);


  constructor() {
    this.foot = 'left';
    this.hardness = Hardness.Medium;
    this.id = this.designId = 0;
  }

  static cloneFrom(original: Addition): Addition {
    const copy = new Addition();

    copy.mesh = original.mesh ? original.mesh.clone() : new THREE.Mesh();
    copy.viewMesh = original.viewMesh ? original.viewMesh.clone() : new THREE.Mesh();
    copy.cutMesh = original.cutMesh ? original.cutMesh.clone() : new THREE.Mesh();
    copy.jointGeometry = original.jointGeometry ? original.jointGeometry.clone() : null;
    copy.lastCutMesh = original.lastCutMesh ? original.lastCutMesh.clone() : null;

    copy.name = original.name;
    copy.fileName = original.fileName;
    copy.groupName = original.groupName;
    copy.id = original.id;
    copy.designId = original.designId;
    copy.designAdditionId = original.designAdditionId;
    copy.color = original.color;
    copy.angle = original.angle;
    copy.status = original.status;
    copy.isUpdated = original.isUpdated;
    copy.isReverse = original.isReverse;
    copy.foot = original.foot;
    copy.hardness = original.hardness;
    copy.centerIndex = original.centerIndex;
    copy.centerIndexOnViewerMesh = original.centerIndexOnViewerMesh;
    copy.automaticAddition = original.automaticAddition;
    copy.thickness = original.thickness
    copy.mode = original.mode;

    copy.height = BoundedValue.cloneFrom(original.height);
    copy.meshPosition = original.meshPosition.clone();
    copy.defaultRelativeLocation = original.defaultRelativeLocation.clone();
    copy.scale = original.scale.clone();
    copy.boxOrigin = original.boxOrigin.clone();

    copy.boundariesIndex = [...original.boundariesIndex];
    copy.debugLines = original.debugLines.map(line => line.clone());
    copy.debugSpheres = original.debugSpheres.map(sphere => sphere.clone());
    copy.debug_shape = original.debug_shape ? original.debug_shape.clone() : null;
    copy.groupNameAceess = original.groupNameAceess;
    copy.paintType = original.paintType;
    copy.relativeIndexMesh = original.relativeIndexMesh;
    copy.additionCenter = original.additionCenter;

    return copy;
  }

  loadAddition = async (loadJson: boolean = false) => {
    if (loadJson) await this.readJsonAddition(`/assets/Additions/${this.fileName}.json`);
    //console.log("this.fileName", this.fileName);
    let addition1 = await LoadStl(`/assets/Additions/${this.fileName}.stl`);
    if (addition1 == null) throw new Error("Addition file not found");

    this.preprocess(addition1);
  }

  loadAdditionFromBufferGeo = (buffer: THREE.BufferGeometry) => {
    this.preprocess(buffer);
  }

  preprocess = (additionTemp: THREE.BufferGeometry) => {
    //additionTemp.scale(1000, 1000, 1000);
    const duplicatesRemoved1 = clearDuplicateVertices(additionTemp);
    //console.log(duplicatesRemoved1.attributes.position.count);
    let mesh = new THREE.Mesh(duplicatesRemoved1, DEFAULT_ADDITON_MESH_MATERIAL.clone());

    mesh.geometry.computeBoundsTree();
    mesh.geometry.computeVertexNormals();

    if (this.foot === 'right') {
      mesh.geometry.scale(-1, 1, 1);
    }

    this.mesh = mesh;
    if (mesh.geometry.boundingBox) {
      this.boxOrigin = mesh.geometry.boundingBox
    }
    this.mesh.position.set(this.meshPosition.x, this.meshPosition.y, this.meshPosition.z);
    this.mesh.scale.x = this.scale.x;
    this.mesh.scale.z = this.scale.z;
    this.SetHeight(this.height.value);
    this.mesh.rotateY(this.angle);


    ///for debug . set to be under the insole
    // this.mesh.translateY(-10);
    this.mesh.position.setY(-10);

    this.mesh.updateMatrixWorld();

    this.SetViewMesh();
    this.viewMesh.position.set(this.meshPosition.x, this.meshPosition.y, this.meshPosition.z);
  }
  SetHeight(newheight: number) {
    //if(newheight === this.height) return;
    // const newheight_inrange = Math.min(Math.max(newheight, HEIGHT_MIN + 0.001), HEIGHT_MAX);
    // this.height.setValue(newheight_inrange);
    // this.height_scale.setValue(newheight_inrange/20 + 0.1); 
    // const scale_y = this.SetScaleBasedonHeight();
    // this.scale = new THREE.Vector3(1, scale_y, 1);
    this.height.setValue(newheight);
    const scale_y = this.SetScaleBasedonHeight();
    this.scale = new THREE.Vector3(this.scale.x, scale_y, this.scale.z);
    return this.height;
  }

  SetScaleBasedonHeight() {
    //console.log(`scale: ${this.fileName}, height: ${this.height}`);
    const box = new THREE.Box3().setFromObject(this.mesh);
    let scaleY = this.height.value / this.boxOrigin.getSize(new THREE.Vector3()).y;
    this.mesh.scale.y = scaleY;
    const box2 = new THREE.Box3().setFromObject(this.mesh);

    //console.log(`scale: ${scale}, boxmin: ${box.min.y}, boxmax: ${box.max.y}, boxsize: ${box.getSize(new THREE.Vector3()).y}`);
    //console.log(`boxmin2: ${box2.min.y}, boxmax2: ${box2.max.y}, boxsize2: ${box2.getSize(new THREE.Vector3()).y}`);
    return scaleY;
  }

  toJSON() {
    //console.log("toJSON", this)
    return {
      name: this.name,
      groupName: this.groupName,
      height: this.height.value,
      location: {
        x: this.meshPosition.x,
        y: this.meshPosition.y,
        z: this.meshPosition.z,
      },
      additionId: this.id,
      footSide: this.foot,
      hardness: HardnessValueMap[this.hardness],
      scale: {
        x: this.scale.x,
        y: this.scale.y,
        z: this.scale.z
      },
      center: {
        x: 0,
        y: 0,
        z: 0
      },
      relativeLocation: {
        x: this.defaultRelativeLocation.x,
        y: this.defaultRelativeLocation.y,
        z: this.defaultRelativeLocation.z
      },
      angle: this.angle.toFixed(3),
      isReverse: this.isReverse,
      iMode: this.mode
    };
  }

  loadFromJson = (jsonData: any, designCategory?: DesignCategory) => {
    if ("id" in jsonData) {
      this.id = jsonData.id;
    }
    if ("designAdditionId" in jsonData) {
      this.designAdditionId = jsonData.designAdditionId;
    }
    if ("designId" in jsonData) {
      this.designId = jsonData.designId;
    }

    if ("name" in jsonData) {
      this.name = jsonData.name;
      this.relativeIndexMesh = this.getRelativeIndexMesh(designCategory);
    }

    if ("groupName" in jsonData) {
      this.groupName = jsonData.groupName;
      //this.SetColor();
    }

    if ("height" in jsonData) {
      this.height = new BoundedValue(jsonData.height, HEIGHT_MIN, FOREFOOT_ADDITION_NAMES.includes(this.name) ? HEIGHT_MAX_FOREFOOT : HEIGHT_MAX);
    }

    if ("angle" in jsonData) {
      this.angle = jsonData.angle;
    }


    if ("isReverse" in jsonData) {
      this.isReverse = jsonData.isReverse;
    }

    if ("centerIndex" in jsonData) {
      this.centerIndex = jsonData.centerIndex;
    }

    if ("footSide" in jsonData) {
      this.foot = jsonData.footSide.toLowerCase();
    }

    if ("hardness" in jsonData) {
      this.hardness = Hardness[jsonData.hardness as keyof typeof Hardness];
    }

    if ("groupName" in jsonData) {
      this.groupName = jsonData.groupName;
    }

    if ("relativeLocation" in jsonData) {
      this.defaultRelativeLocation.set(
        jsonData.relativeLocation.x,
        jsonData.relativeLocation.y,
        jsonData.relativeLocation.z
      );
    }

    if ("location" in jsonData && jsonData.location !== null && jsonData.location !== undefined) {
      this.meshPosition.set(
        jsonData.location.x,
        jsonData.location.y,
        jsonData.location.z
      );
    }

    if ("scale" in jsonData) {
      this.scale.set(
        jsonData.scale.x,
        jsonData.scale.y,
        jsonData.scale.z
      );
    }

    if ('iMode' in jsonData) {
      this.mode = jsonData.iMode;
    }
    if ('groupNameAceess' in jsonData) {
      this.groupNameAceess = jsonData.groupId;
    }
    if ('color' in jsonData) {
      this.paintType = AdditionPaintType.ByConstant;
      if (jsonData.color == null || jsonData.color == undefined || jsonData.color == "") {
        this.paintType = AdditionPaintType.ByHardness;
        this.SetColor();
        //set colorr by hardness
      } else {
        this.color = Number(jsonData.color.replace("#", "0x"));
      }
    }
  }


  readJsonAddition = async (file: string) => {
    const response = await fetch(file);
    const data = await response.json();
    this.loadFromJson(data);
  }

  SetRelativeLocation(pos: THREE.Vector3, insoleMesh: THREE.Mesh) {
    const insoleBox = GetInsoleBox(insoleMesh);
    const width = insoleBox.getSize(new THREE.Vector3).x;
    const len = insoleBox.getSize(new THREE.Vector3).z;
    const additonBox = this.mesh.geometry.boundingBox;
    if (additonBox == null) return;
    const additionCenter = additonBox.getCenter(new THREE.Vector3);
    const percent = width * (this.foot == 'right' ? pos.x : 1 - pos.x);
    const defaultPos = new THREE.Vector3(insoleBox.min.x + percent, 0, insoleBox.max.z - len * pos.z);

    this.mesh.translateX(defaultPos.x - additionCenter.x);
    this.mesh.translateZ(defaultPos.z - additionCenter.z);

    this.meshPosition = this.mesh.position;
    //console.log('translation val: ', defaultPos.x - additionCenter.x, defaultPos.z - additionCenter.z);
  }

  getPositionOfIndex(index: number, buffer: THREE.BufferGeometry): THREE.Vector3 {
    const position = buffer.attributes.position;

    if (index < 0 || index >= position.count) {
      throw new RangeError(`Vertex index ${index} is outside the geometry's ${position.count} vertices.`);
    }

    return new THREE.Vector3().fromBufferAttribute(position, index);
  }


  setLocationByIndex(bufferGm: THREE.BufferGeometry) {
    if (this.relativeIndexMesh === null) return;

    const markerPositionOnMesh = this.getPositionOfIndex(this.relativeIndexMesh, bufferGm);

    // const debugGeometry = new THREE.SphereGeometry(3, 8, 8)
    // const debugMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 })
    // const debugSphere = new THREE.Mesh(debugGeometry, debugMaterial)
    // debugSphere.position.copy(markerPositionOnMesh)
    // this.debugSpheres.push(debugSphere)

    const additionBox = new THREE.Box3().setFromObject(this.mesh);
    if (additionBox == null) return;
    // const additionCenter = additionBox.getCenter(new THREE.Vector3);
    const width = additionBox.getSize(new THREE.Vector3).x;
    const len = additionBox.getSize(new THREE.Vector3).z;

    const percent = width * (this.foot == 'right' ? this.defaultRelativeLocation.x : 1 - this.defaultRelativeLocation.x);
    const defaultPos = new THREE.Vector3(additionBox.min.x + percent, 0, additionBox.max.z - len * this.defaultRelativeLocation.z);

    this.mesh.translateX(markerPositionOnMesh.x - defaultPos.x);
    this.mesh.translateZ(markerPositionOnMesh.z - defaultPos.z);

    this.meshPosition = this.mesh.position;
  }

  TranslateAdditionFromLowpolyToMainMesh = (lowpolymesh: THREE.BufferGeometry, outputMesh: THREE.Mesh) => {
    this.mesh.geometry.computeBoundingBox();
    if (this.relativeIndexMesh === null) this.SetRelativeLocation(this.defaultRelativeLocation, outputMesh);
    if (this.relativeIndexMesh !== null) this.setLocationByIndex(lowpolymesh);
  }
  
  UpdateAddition(outputMesh: THREE.Mesh, newHeight: number | null, doApplyViewMeshOnOutputMesh: boolean = true, trans: THREE.Vector3 | null = null) {
    if (this.mesh.geometry.boundingBox != null) {
      if (newHeight !== null) {
        this.SetHeight(newHeight);
      }

      //this.meshPosition = this.mesh.position.clone().applyMatrix4(this.mesh.matrixWorld);
      if (trans != null) {
        this.mesh.position.add(trans);
        this.meshPosition = this.mesh.position.clone();
        this.isUpdated = true;
      }

      this.mesh.updateMatrixWorld();
      this.SetViewMesh();

      if (newHeight === null)
        this.ApplyAddition(outputMesh, doApplyViewMeshOnOutputMesh);
    }
    else console.error("can not update addition, no bounding box");
  }

  // InitRondomAddition() {
  //   if (this.groupName.toLocaleLowerCase() === 'Rondom') {
  //     if (this.name == 'Pelotte 2') {
  //       this.SetHeight(7);
  //       this.hardness = Hardness.ExtraSoft;
  //     }
  //     else if (this.name == 'Pelotte 3') {
  //       this.SetHeight(8);
  //       this.hardness = Hardness.ExtraSoft;
  //     }
  //     else if (this.name == 'Pelotte 4 vlak') {
  //       this.SetHeight(7);
  //       this.hardness = Hardness.ExtraSoft;
  //     }
  //     else if (this.name == 'Pelotte 4') {
  //       this.SetHeight(8);
  //       this.hardness = Hardness.ExtraSoft;
  //     }
  //   }
  // }
  // UndoAddition(outputMesh: THREE.Mesh) {
  //   console.log("UndoAddition");
  //   const positionAttribute = outputMesh.geometry.getAttribute('position');
  //   const updatedPositions = new Float32Array(positionAttribute.array.length);
  //   updatedPositions.set(positionAttribute.array);

  //   for (let i = 0; i < this.effectedVertices.length; i++) {
  //     const vertex = new THREE.Vector3();
  //     vertex.fromBufferAttribute(positionAttribute, this.effectedVertices[i].idx);
  //     updatedPositions[this.effectedVertices[i].idx * 3 + 1] -= this.effectedVertices[i].val;
  //   }

  //   positionAttribute.array.set(updatedPositions);
  //   positionAttribute.needsUpdate = true;
  // }

  SetViewMesh() {
    // const positionAttribute = this.mesh.geometry.getAttribute('position');
    // const boundaryVertices: THREE.Vector3[] = [];
    // this.mesh.geometry.computeBoundingBox();

    // for (let i = 0; i < this.boundariesIndex.length; i++) {
    //   const vertex = new THREE.Vector3();
    //   vertex.fromBufferAttribute(positionAttribute, this.boundariesIndex[i]);
    //   let worldVertex = new THREE.Vector3(vertex.x, this.mesh.geometry.boundingBox?.min.y, vertex.z).applyMatrix4(this.mesh.matrixWorld);
    //   boundaryVertices.push(worldVertex);
    // }
    // boundaryVertices.push(boundaryVertices[0]);
    // this.viewCurve = createLineShapeFromPoints(boundaryVertices, 0x000000, 5);
    this.SetColor();

    let material: THREE.Material = DEFAULT_ADDITON_MESH_MATERIAL.clone();
    if ('color' in material) {
      (material as any).color.set(this.color);
    }
    if (this.viewMesh && this.viewMesh.material) {
      material = Array.isArray(this.viewMesh.material)
        ? this.viewMesh.material[0]
        : this.viewMesh.material;
    }

    const newMesh = cloneMeshWithTransform(this.mesh, material);
    // Set color on the mesh material if it has a color property
    if (Array.isArray(this.mesh.material)) {
      this.mesh.material.forEach(mat => {
        if ('color' in mat) {
          (mat as any).color.set(this.color);
        }
      });
    } else if ('color' in this.mesh.material) {
      (this.mesh.material as any).color.set(this.color);
    }

    if (this.viewMesh) {
      this.viewMesh.copy(newMesh, true);
    }
    else {
      this.viewMesh = newMesh;
    }
    return newMesh;
  }

  SetColor() {
    if (this.paintType === AdditionPaintType.ByHardness) {
      if (this.hardness === Hardness.Soft) this.color = 0xfde019;
      else if (this.hardness === Hardness.ExtraSoft) this.color = 0xff6600;
      else this.color = 0x494541;
    }

  }

  // SetColor() {
  //   this.color = 0xff6600;
  //   return;

  //   if (this.groupName == "Dancers Pad") {
  //     this.color = 0xcc00cc;
  //   } else if (this.groupName == "Heel") {
  //     this.color = 0xcc00cc; // warmer orange
  //     // this.selectedColor = 0xff8f2b;
  //   } else if (this.groupName == "Lateral Pronate Pads") {
  //     this.color = 0xff6600;
  //   } else if (this.groupName == "Met Bars") {
  //     this.color = 0xff6600;
  //   } else if (this.groupName == "Met Domes") {
  //     this.color = 0xff6600;
  //   } else if (this.groupName == "Metatarsal Pads") {
  //     this.color = 0xff0000;
  //   } else if (this.groupName == "Others") {
  //     this.color = 0xff0000;
  //   } else if (this.groupName == "Plantar Metatarsal Pads") {
  //     this.color = 0xff0000;
  //   } else if (this.groupName == "Reliefs") {
  //     this.color = 0xff0000;
  //   }
  // }

  ApplyViewMeshOnOutputMesh(outputMesh: THREE.Mesh, viewMeshUpdated: THREE.Mesh, offsetMultipleAdditions: number = 0) {
    const prevMat = this.viewMesh
      ? (Array.isArray(this.viewMesh.material) ? this.viewMesh.material[0] : this.viewMesh.material)
      : null;

    const additionMaterial = (prevMat
      ? prevMat.clone()
      : DEFAULT_ADDITON_MESH_MATERIAL.clone()) as any;

    additionMaterial.transparent = true;           // keep opacity effective
    if (additionMaterial.color) {
      additionMaterial.color.setHex(this.color);   // repaint base group color
    }

    // Validate input meshes
    if (!this.validateMesh(outputMesh, 'outputMesh') || !this.validateMesh(viewMeshUpdated, 'inputMesh')) {
      console.error("Invalid input meshes for ApplyViewMeshOnOutputMesh");
      return;
    }

    if (viewMeshUpdated.scale.y == 0) {
      viewMeshUpdated.scale.y = 0.1;
      viewMeshUpdated.updateMatrixWorld();
    }
    //console.log("viewMeshUpdated.scale", viewMeshUpdated.scale);

    // 1. Generate input mesh extrusion
    let start_time = performance.now();
    const inputMeshEx = extrudeAlongY(viewMeshUpdated, 100);
    // Validate the generated mesh
    if (!inputMeshEx) {
      console.error("Generated inputMeshEx is null");
      return;
    }
    if (!this.validateMesh(inputMeshEx, 'inputMeshEx')) {
      console.error("Generated inputMeshEx is invalid");
      return;
    }

    inputMeshEx.position.copy(viewMeshUpdated.position);
    inputMeshEx.rotation.copy(viewMeshUpdated.rotation);
    inputMeshEx.updateMatrix();
    inputMeshEx.updateMatrixWorld();
    let end_time = performance.now();
    //console.log("GenerateCoverExperimental time: ", end_time - start_time);
    start_time = performance.now();

    // 2. Flip outputmesh and addition if normals are inward
    let out: THREE.Mesh | null = null;
    let inp: THREE.Mesh | null = null;
    out = outputMesh.clone();
    inp = inputMeshEx!.clone();

    if (this.foot === 'right') {
      out.geometry = outputMesh.geometry.clone();
      if (Array.isArray(outputMesh.material)) {
        out.material = outputMesh.material.map(mat => mat.clone());
      } else {
        out.material = outputMesh.material.clone();
      }
      if (areNormalsInverted(out.geometry))
        flipFaceNormals(out.geometry);
    } else {
      inp.geometry = inputMeshEx!.geometry.clone();
      if (Array.isArray(inputMeshEx!.material)) {
        inp.material = inputMeshEx!.material.map(mat => mat.clone());
      } else {
        inp.material = inputMeshEx!.material.clone();
      }
      if (areNormalsInverted(out.geometry))
        flipFaceNormals(out.geometry);

      flipFaceNormals(inp.geometry);
    }

    end_time = performance.now();
    //console.log("Flip outputmesh and addition time: ", end_time - start_time);
    start_time = performance.now();

    // 3. Intersection
    const meshA = prepareMeshForCSG(inp);
    const meshB = prepareMeshForCSG(out);

    // Validate meshes before CSG operations
    if (!this.validateMesh(meshA, 'meshA') || !this.validateMesh(meshB, 'meshB')) {
      console.error("Invalid meshes for CSG operations");
      return;
    }

    try {
      meshA.geometry.computeBoundsTree();
      meshB.geometry.computeBoundsTree();
    } catch (error) {
      console.error("Failed to compute bounds tree:", error);
      return;
    }

    // Create CSG evaluator for faster operations
    const csgEvaluator = new Evaluator();
    csgEvaluator.attributes = ['position', 'normal'];

    // Convert meshes to Brush objects for faster CSG operations
    const brushA = new Brush(meshA.geometry, meshA.material);
    const brushB = new Brush(meshB.geometry, meshB.material);

    // Apply the same transforms as the original meshes
    brushA.position.copy(meshA.position);
    brushA.rotation.copy(meshA.rotation);
    brushA.scale.copy(meshA.scale);
    brushA.updateMatrix();
    brushA.updateMatrixWorld();

    brushB.position.copy(meshB.position);
    brushB.rotation.copy(meshB.rotation);
    brushB.scale.copy(meshB.scale);
    brushB.updateMatrix();
    brushB.updateMatrixWorld();

    // Perform the intersection operation
    let intResBrush;
    try {
      intResBrush = csgEvaluator.evaluate(brushA, brushB, INTERSECTION);
    } catch (error) {
      console.error("CSG evaluation failed:", error);
      return;
    }

    if (!intResBrush || !intResBrush.geometry) {
      console.error("CSG evaluation returned invalid result");
      return;
    }

    // Convert Brush to THREE.Mesh
    const intRes = new THREE.Mesh(intResBrush.geometry, intResBrush.material);
    intRes.position.copy(intResBrush.position);
    intRes.rotation.copy(intResBrush.rotation);
    intRes.scale.copy(intResBrush.scale);
    intRes.updateMatrix();
    intRes.updateMatrixWorld();

    const mergedGeometry = BufferGeometryUtils.mergeVertices(intRes.geometry);
    intRes.geometry.dispose();
    intRes.geometry.copy(mergedGeometry);
    intRes.geometry.computeVertexNormals();
    const outputNormalAttribute = intRes.geometry.getAttribute('normal');
    if (outputNormalAttribute) {
      outputNormalAttribute.needsUpdate = true;
    }

    end_time = performance.now();
    //console.log("Intersection time: ", end_time - start_time);
    start_time = performance.now();

    // 4. Show intersection mesh
    // Create a new pink material for the intRes mesh
    intRes.material = additionMaterial;
    // Ensure stable render order across groups


    // if (this.lastIntRes) {``
    //   scene.remove(this.lastIntRes);
    //   this.lastIntRes.geometry.dispose();
    //   if (this.lastIntRes.material instanceof THREE.Material) this.lastIntRes.material.dispose();
    // }

    // Scale around center: preserve world-space center while shrinking X/Z
    const centerBefore = new THREE.Box3().setFromObject(intRes).getCenter(new THREE.Vector3());
    // const offset = this.id / 100;
    // const offset = 0;
    //intRes.scale.set(1.05, 1.05, 1.05);
    intRes.updateMatrixWorld(true);
    const centerAfter = new THREE.Box3().setFromObject(intRes).getCenter(new THREE.Vector3());
    intRes.position.add(centerBefore.sub(centerAfter));
    intRes.updateMatrixWorld(true);
    const worldBBox = new THREE.Box3().setFromObject(intRes);
    const minY = worldBBox.min.y; // world-space min Y
    // console.log("min y of intRes", minY);
    intRes.position.y = offsetMultipleAdditions + 0.5;

    // scene.add(intRes);
    // this.lastIntRes = intRes;

    end_time = performance.now();
    //console.log("Show intersection mesh time: ", end_time - start_time);

    function prepareMeshForCSG(mesh: THREE.Mesh): THREE.Mesh {
      mesh.updateMatrix();
      mesh.updateMatrixWorld(true);
      mesh.geometry = mesh.geometry.clone();
      mesh.geometry.applyMatrix4(mesh.matrixWorld);
      mesh.position.set(0, 0, 0);
      mesh.rotation.set(0, 0, 0);
      mesh.scale.set(1, 1, 1);
      mesh.updateMatrix();
      return mesh;
    }

    // Clone the geometry without the position offset for inputMesh
    // const new_intRes = prepareMeshForCSG(intRes);
    const geometryForInputMesh = intRes.geometry.clone();

    // Check if the intersection resulted in a valid geometry
    if (intRes.geometry.getAttribute('position').count === 0) {
      // Use the original viewMesh geometry instead of the failed intersection
      const originalViewMesh = this.viewMesh;
      if (originalViewMesh && originalViewMesh.geometry.getAttribute('position').count > 0) {
        //console.log("qweoriginalViewMesh.geometry.getAttribute('position').count > 0");
        viewMeshUpdated.geometry = originalViewMesh.geometry.clone();
        viewMeshUpdated.geometry.attributes.position.needsUpdate = true;
        viewMeshUpdated.geometry.computeVertexNormals();
      } else {
        //console.log("qweoriginalViewMesh.geometry.getAttribute('position').count <= 0");
        return;
      }
    } else {
      //console.log("qweintRes.geometry.getAttribute('position').count > 0");
      viewMeshUpdated.geometry = geometryForInputMesh;
      viewMeshUpdated.geometry.attributes.position.needsUpdate = true;
      viewMeshUpdated.geometry.computeVertexNormals();
    }

    // Copy the positioning from intRes to the viewMesh

    viewMeshUpdated.position.copy(intRes.position);
    viewMeshUpdated.rotation.copy(intRes.rotation);
    viewMeshUpdated.scale.copy(intRes.scale);
    viewMeshUpdated.updateMatrix();
    viewMeshUpdated.updateMatrixWorld(true);
    viewMeshUpdated.geometry.computeVertexNormals();
    viewMeshUpdated.material = additionMaterial;
    viewMeshUpdated.renderOrder = getAdditionRenderOrder(this.groupName);

    this.viewMesh = viewMeshUpdated;
  }

  ApplyViewMeshOnOutputMeshNEW(profilePositions: THREE.Vector3[], viewMesh: THREE.Mesh, insoleMesh: THREE.Mesh) {
    const prevMat = this.viewMesh
      ? (Array.isArray(this.viewMesh.material) ? this.viewMesh.material[0] : this.viewMesh.material)
      : null;

    const additionMaterial = (prevMat
      ? prevMat.clone()
      : DEFAULT_ADDITON_MESH_MATERIAL.clone()) as any;

    additionMaterial.transparent = true;           // keep opacity effective
    additionMaterial.flatShading = false;
    additionMaterial.side = THREE.DoubleSide;
    if (additionMaterial.color) {
      additionMaterial.color.setHex(this.color);   // repaint base group color
    }
    additionMaterial.needsUpdate = true;

    const newPositions: number[] = [];
    const newIndices: number[] = [];
    let nextIndex = 0;

    const polygon = offsetLineInward(profilePositions, this.foot == 'right' ? -0.75 : 0.75);

    viewMesh.updateMatrixWorld(true);

    const inverseViewMat = new THREE.Matrix4().copy(viewMesh.matrixWorld.clone()).invert();
    const meshMat = viewMesh.matrixWorld.clone();

    const geometry = viewMesh.geometry.clone() as THREE.BufferGeometry;
    geometry.applyMatrix4(meshMat);

    const position = geometry.attributes.position;
    const index = geometry.index;

    insoleMesh.updateMatrixWorld();
    if (!insoleMesh.geometry.boundingBox) {
      insoleMesh.geometry.computeBoundingBox();
    }

    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();

    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        // 2. Apply World Matrix to triangle vertices
        a.fromBufferAttribute(position, index.getX(i));
        b.fromBufferAttribute(position, index.getX(i + 1));
        c.fromBufferAttribute(position, index.getX(i + 2));

        const splitTriangles = processAndSplitTriangle(a, b, c, polygon);
        splitTriangles.forEach(tri => {
          const ia = addVertex(tri[0]);
          const ib = addVertex(tri[1]);
          const ic = addVertex(tri[2]);

          newIndices.push(ia, ib, ic);
        });
      }
    } else {
      for (let i = 0; i < position.count; i += 3) {
        a.fromBufferAttribute(position, i);
        b.fromBufferAttribute(position, i + 1);
        c.fromBufferAttribute(position, i + 2);

        const splitTriangles = processAndSplitTriangle(a, b, c, polygon);
        splitTriangles.forEach(tri => {
          const ia = addVertex(tri[0]);
          const ib = addVertex(tri[1]);
          const ic = addVertex(tri[2]);

          newIndices.push(ia, ib, ic);
        });
      }
    }

    // 2. Determine a safe starting height for the downward rays
    const safeStartY = insoleMesh.geometry.boundingBox
      ? insoleMesh.geometry.boundingBox.max.y + 100
      : 1000;

    const selectedArray = insoleMesh.geometry.getAttribute('selected').array;

    // 3. Prepare reusable raycasting objects to prevent memory leaks
    const raycaster = new THREE.Raycaster();
    const rayDirection = new THREE.Vector3(0, -1, 0); // Pointing straight down
    const rayOrigin = new THREE.Vector3();

    insoleMesh.geometry.computeBoundsTree();

    for (let i = 0; i < newPositions.length; i += 3) {
      const x = newPositions[i];
      const z = newPositions[i + 2];

      rayOrigin.set(x, safeStartY, z);
      raycaster.set(rayOrigin, rayDirection);

      const intersects = raycaster.intersectObject(insoleMesh, false);

      if (intersects.length > 0) {
        var id = 0;
        do {
          if (
            selectedArray[intersects[id].face!.a] > 0.9 ||
            selectedArray[intersects[id].face!.b] > 0.9 ||
            selectedArray[intersects[id].face!.c] > 0.9
          ) {
            newPositions[i + 1] = intersects[id].point.y;
            break;
          }
          else
            id++;
        } while (intersects.length > id);
      }
      else {
        // If the ray misses (the vertex hangs off the edge of mainMesh), 
        // it simply keeps its mathematically interpolated Y from the clipping step.
        console.log("!!!!", x, z);
      }
    }

    var updatedGeometry = new THREE.BufferGeometry();
    updatedGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
    updatedGeometry.setIndex(new THREE.Uint32BufferAttribute(newIndices, 1));
    // updatedGeometry.applyMatrix4(inverseViewMat);

    const weldedGeometry = BufferGeometryUtils.mergeVertices(updatedGeometry);
    weldedGeometry.computeVertexNormals();

    this.viewMesh.position.set(0, 2, 0);
    this.viewMesh.rotation.set(0, 0, 0);
    this.viewMesh.scale.set(1, 1, 1);
    this.viewMesh.updateMatrix();
    this.viewMesh.updateMatrixWorld(true);

    this.viewMesh.geometry.dispose();
    this.viewMesh.geometry = weldedGeometry;
    this.viewMesh.material = additionMaterial;
    this.viewMesh.renderOrder = getAdditionRenderOrder(this.groupName);
    this.viewMesh.geometry.computeVertexNormals();
    this.viewMesh.geometry.computeBoundingBox();
    this.viewMesh.geometry.computeBoundingSphere();

    function addVertex(v: THREE.Vector3): number {
      newPositions.push(v.x, v.y, v.z);
      return nextIndex++;
    }

    function processAndSplitTriangle(tA: THREE.Vector3, tB: THREE.Vector3, tC: THREE.Vector3, polygonPoints: THREE.Vector2[]) {

      // 1. Check which vertices are inside the curve
      const A_in = isPointInPolygon(tA, polygonPoints);
      const B_in = isPointInPolygon(tB, polygonPoints);
      const C_in = isPointInPolygon(tC, polygonPoints);

      // If all vertices are inside, return the original triangle intact
      if (A_in && B_in && C_in) {
        return [[tA.clone(), tB.clone(), tC.clone()]];
      }

      // If all vertices are outside, and no intersections exist, it's completely outside
      // (Note: To be perfectly robust, you'd check if the curve is entirely *inside* the triangle here)

      // 2. Find intersections and build the clipped polygon
      const edges = [
        { start: tA.clone(), end: tB.clone(), inside: A_in },
        { start: tB.clone(), end: tC.clone(), inside: B_in },
        { start: tC.clone(), end: tA.clone(), inside: C_in }
      ];

      const newPolygonPoints: THREE.Vector3[] = [];

      edges.forEach(edge => {
        // If the starting point of the edge is inside, keep it
        if (edge.inside) {
          newPolygonPoints.push(edge.start);
        }

        // Check this triangle edge against every edge of the curve
        const intersections = [];
        for (let i = 0; i < polygonPoints.length; i++) {
          const p1 = polygonPoints[i];
          const p2 = polygonPoints[(i + 1) % polygonPoints.length]; // Loop back to start

          const intersect = getSegmentIntersection(edge.start, edge.end, p1, p2);
          if (intersect) {
            intersections.push(intersect);
          }
        }

        // Sort intersections so they are added in order of distance from the edge's start point
        intersections.sort((a, b) => a.distanceTo(edge.start) - b.distanceTo(edge.start));
        newPolygonPoints.push(...intersections);
      });

      // 3. Triangulate the resulting shape
      // A standard "Triangle Fan" approach works well for simple convex clippings
      const newTriangles = [];
      if (newPolygonPoints.length >= 3) {
        for (let i = 1; i < newPolygonPoints.length - 1; i++) {
          newTriangles.push([
            newPolygonPoints[0],
            newPolygonPoints[i],
            newPolygonPoints[i + 1]
          ]);
        }
      }

      return newTriangles;
    }

    function getSegmentIntersection(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector2, p3: THREE.Vector2) {
      const s1_x = p1.x - p0.x, s1_y = p1.z - p0.z; // s1_y is structurally Z
      const s2_x = p3.x - p2.x, s2_y = p3.y - p2.y; // p2/p3.y store Z from the Vector2 mapping

      const denominator = -s2_x * s1_y + s1_x * s2_y;
      if (denominator === 0) return null; // Collinear or parallel

      const s = (-s1_y * (p0.x - p2.x) + s1_x * (p0.z - p2.y)) / denominator;
      const t = (s2_x * (p0.z - p2.y) - s2_y * (p0.x - p2.x)) / denominator;

      if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
        return new THREE.Vector3(
          p0.x + (t * s1_x),
          p0.y + (t * (p1.y - p0.y)),
          p0.z + (t * s1_y)
        );
      }
      return null;
    }

    function isPointInPolygon(point: THREE.Vector3, polygonPoints: THREE.Vector2[]) {
      let inside = false;
      for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
        const xi = polygonPoints[i].x, yi = polygonPoints[i].y;
        const xj = polygonPoints[j].x, yj = polygonPoints[j].y;

        const intersect = ((yi > point.z) !== (yj > point.z)) &&
          (point.x < (xj - xi) * (point.z - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    }

    function offsetLineInward(line: THREE.Vector3[], offsetDistance: number = 1.0) {
      const points: THREE.Vector2[] = [];
      const count = line.length;

      // 1. Extract 2D points (X, Z)
      for (let i = 0; i < line.length; i++) {
        points.push(new THREE.Vector2(line[i].x, line[i].z));
      }

      const newPoints: THREE.Vector2[] = [];

      // 2. Calculate normals and offset each vertex inward
      for (let i = 0; i < count; i++) {
        const prev = points[(i - 1 + count) % count];
        const curr = points[i];
        const next = points[(i + 1) % count];

        // Compute tangents for previous and next segments
        const t1 = new THREE.Vector2().subVectors(curr, prev).normalize();
        const t2 = new THREE.Vector2().subVectors(next, curr).normalize();

        // Compute vertex normal (average of normalized perpendiculars)
        // Rotate tangent 90 degrees to get the normal
        const n1 = new THREE.Vector2(-t1.y, t1.x);
        const n2 = new THREE.Vector2(-t2.y, t2.x);
        const normal = new THREE.Vector2().addVectors(n1, n2).normalize();

        // Determine polygon winding order to ensure we go INWARD instead of outward
        // (Assuming counter-clockwise or clockwise; adjust sign if it expands instead)
        const newPoint = curr.clone().addScaledVector(normal, -offsetDistance);
        newPoints.push(newPoint);
      }

      return newPoints;
    }
  }












  validateMesh(mesh: THREE.Mesh, meshName: string): boolean {
    if (!mesh || !mesh.geometry) {
      console.error(`${meshName}: Mesh or geometry is null/undefined`);
      return false;
    }

    const positionAttr = mesh.geometry.getAttribute('position');
    if (!positionAttr || positionAttr.count === 0) {
      console.error(`${meshName}: Missing or empty position attribute`);
      return false;
    }

    const indexAttr = mesh.geometry.getIndex();
    if (!indexAttr || indexAttr.count === 0) {
      console.error(`${meshName}: Missing or empty index attribute`);
      return false;
    }

    // Check for valid vertex data
    const positions = positionAttr.array;
    for (let i = 0; i < Math.min(positionAttr.count * 3, 100); i++) {
      if (isNaN(positions[i]) || !isFinite(positions[i])) {
        console.error(`${meshName}: Invalid vertex data at index ${i}`);
        return false;
      }
    }

    return true;
  }

  findCenterIndexOnOtherMesh(outputMesh: THREE.Mesh) {
    const selectedAttribute = outputMesh.geometry.getAttribute('selected');
    const positionAttribute = this.mesh.geometry.getAttribute('position');
    if (!selectedAttribute) {
      console.error("can not find center index on other mesh, no selected attribute");
      return [];
    };

    const selectedArray = selectedAttribute.array;
    const center = new THREE.Vector3(
      positionAttribute.getX(this.centerIndex),
      positionAttribute.getY(this.centerIndex),
      positionAttribute.getZ(this.centerIndex)
    );
    const raycaster = new THREE.Raycaster();
    const rayDirection = new THREE.Vector3(0, 200, 0);
    const worldVertex = center.clone().applyMatrix4(this.mesh.matrixWorld);
    raycaster.set(worldVertex, rayDirection);
    const intersects = raycaster.intersectObject(outputMesh);

    for (let i = 0; i < intersects.length; i++) {
      const centerFaceIndex = intersects[i].faceIndex;
      if (centerFaceIndex === undefined || centerFaceIndex === null) continue;
      const indexAttribute = outputMesh.geometry.getIndex();
      if (!indexAttribute) continue;
      const a = indexAttribute.getX(centerFaceIndex * 3);
      const b = indexAttribute.getX(centerFaceIndex * 3 + 1);
      const c = indexAttribute.getX(centerFaceIndex * 3 + 2);
      // Check if any of these vertices are selected
      if (selectedArray[a] > 0 || selectedArray[b] > 0 || selectedArray[c] > 0) {
        this.centerIndexOnViewerMesh = a;
        //console.log(`centerIndexOnOtherMesh: `, this.centerIndexOnViewerMesh);
        break;
      }
    }
  }

  getJointGeometry = (outputMesh: THREE.Mesh) => {
    const additionMaterial = new THREE.MeshBasicMaterial({
      color: 0x002E5BBC,
      transparent: true,
      opacity: 1
    });
    if (outputMesh.geometry.attributes.position == null) return null;
    const insoleVertices = outputMesh.geometry.attributes.position.array;
    const sphere = new THREE.SphereGeometry(0);

    const centerVertexId = this.centerIndexOnViewerMesh;
    if (centerVertexId !== undefined) {
      const localX = insoleVertices[centerVertexId * 3];
      const localY = insoleVertices[centerVertexId * 3 + 1];
      const localZ = insoleVertices[centerVertexId * 3 + 2];

      const worldPosition = new THREE.Vector3(localX, localY, localZ);
      worldPosition.applyMatrix4(outputMesh.matrixWorld);

      let sphereMesh = new THREE.Mesh(sphere, additionMaterial.clone());
      sphereMesh.position.copy(worldPosition);
      sphereMesh.position.y += 1;
      sphereMesh.renderOrder = 1;
      sphereMesh.userData.jointType = 'addition';
      sphereMesh.userData.originalPosition = new THREE.Vector3(worldPosition.x, worldPosition.y, worldPosition.z);
      // if(this.jointGeometry != null) 
      //   this.jointGeometry.geometry.dispose();
      this.jointGeometry = sphereMesh;
      return sphereMesh;
    }
    return null;
  }
  UpdateJointGeometry() {
    if (this.jointGeometry == null) return;
    const worldPosition = new THREE.Vector3();
    this.jointGeometry.getWorldPosition(worldPosition);
    this.jointGeometry.userData.originalPosition = new THREE.Vector3(
      worldPosition.x,
      worldPosition.y,
      worldPosition.z
    );
  }

  TryTranslateXZ(outputMesh: THREE.Mesh, doApplyViewMeshOnOutputMesh: boolean = true): boolean {
    this.isUpdated = true;
    //console.log("tranlateXZ original:", this.jointGeometry?.userData.originalPosition);
    //console.log("tranlateXZ position:", this.jointGeometry?.position);
    if (this.jointGeometry === null) return false;
    const currentWorld = new THREE.Vector3();
    this.jointGeometry.getWorldPosition(currentWorld);
    const displacementX = currentWorld.x - this.jointGeometry.userData.originalPosition.x;
    const displacementZ = currentWorld.z - this.jointGeometry.userData.originalPosition.z;

    this.viewMesh.geometry.computeBoundingBox();
    const additionBB = this.viewMesh.geometry.boundingBox?.clone();
    // const additionBB = new THREE.Box3().setFromObject(this.viewMesh);
    additionBB?.translate(new THREE.Vector3(displacementX, 0, displacementZ));
    const insoleBB = outputMesh.geometry.boundingBox;

    if (!additionBB || !insoleBB?.intersectsBox(additionBB)) {
      return false;
    }

    if (Math.abs(displacementX) < 1 && Math.abs(displacementZ) < 1) {
      return false;
    }
    // console.log("displacementX:", displacementX, "displacementZ:", displacementZ);
    // Convert world-space displacement to the mesh's local space to ensure world-aligned translation
    const displacementWorld = new THREE.Vector3(displacementX, 0, displacementZ);
    const worldQuat = new THREE.Quaternion();
    this.mesh.getWorldQuaternion(worldQuat);
    const localDelta = displacementWorld.clone().applyQuaternion(worldQuat.invert());
    this.mesh.translateX(localDelta.x);
    this.mesh.translateZ(localDelta.z);
    this.mesh.updateMatrixWorld();
    this.meshPosition = this.mesh.position;
    this.UpdateAddition(outputMesh, null, doApplyViewMeshOnOutputMesh);
    return true;
  }

  TryRotateY(outputMesh: THREE.Mesh, deltaRadians: number, doApplyViewMeshOnOutputMesh: boolean = true): boolean {
    if (!deltaRadians || Math.abs(deltaRadians) < 0.05) return false;

    this.mesh.rotateY(deltaRadians);
    this.mesh.updateMatrixWorld();
    this.angle += deltaRadians;
    // console.log("rotateY deltaRadians:", deltaRadians, '  angle: ', this.angle);

    this.UpdateAddition(outputMesh, null, doApplyViewMeshOnOutputMesh);
    return true;
  }

  TryScaleXZ(outputMesh: THREE.Mesh, scaleFactor: number, doApplyViewMeshOnOutputMesh: boolean = true): boolean {
    if (!scaleFactor || Math.abs(scaleFactor - 1) < 0.01) return false;
    this.mesh.scale.x *= scaleFactor;
    this.mesh.scale.z *= scaleFactor;
    this.scale.x *= scaleFactor;
    this.scale.z *= scaleFactor;
    // this.scale.x = this.scale.z = this.mesh.scale.x;

    // Reflect X/Z scale factor into height value for UI as requested
    // console.log("scale scaleFactor: ", this.mesh.scale, "scaleFactor: ", scaleFactor);
    this.mesh.updateMatrixWorld();
    this.UpdateAddition(outputMesh, null, doApplyViewMeshOnOutputMesh);
    return true;
  }

  ScaleY(outputMesh: THREE.Mesh, scaleFactor: number, doApplyViewMeshOnOutputMesh: boolean = true): boolean {
    if (!scaleFactor || Math.abs(scaleFactor - 1) < 0.01) return false;

    //console.log("scaleY* scaleFactor:", scaleFactor);
    const newscale = this.scale.y * scaleFactor;
    let newHeight = (newscale / this.scale.y) * this.height.value;
    newHeight = Math.round(Math.min(Math.max(newHeight, this.height.min), this.height.max));

    // console.log("scaleY:", newscale, 'newheight: ', newHeight);

    this.UpdateAddition(outputMesh, newHeight, true);
    //this.scale.y = newscale;
    //this.height.setValue(newHeight);
    // const new_height_scale = Math.min(Math.max(this.height_scale.value * scaleFactor, HEIGHT_SCALE_MIN), HEIGHT_SCALE_MAX);
    // this.height_scale.setValue(new_height_scale);



    // const newHeight = Math.min(Math.max(this.height_scale.value * 20 - 2, HEIGHT_MIN), HEIGHT_MAX);
    // console.log("scaleY* newHeight:", newHeight);
    // this.height.setValue(newHeight);
    // console.log("scaleY* this.height:", this.height.value);
    //this.UpdateAddition(outputMesh, null, doApplyViewMeshOnOutputMesh);
    return true;
  }

  ScaleWithCenter(scale: number, scalex: number, center: THREE.Vector3) {
    const scaleZ = scale;
    if ((!scaleZ || Math.abs(scaleZ - 1) < 0.01) && (!scalex || Math.abs(scalex - 1) < 0.01)) return false;

    if (this.status !== AdditionNewStatus.removed) {
      this.viewMesh.geometry.computeBoundingBox();
      const additionCenter = this.viewMesh.geometry.boundingBox?.getCenter(new THREE.Vector3()) ?? new THREE.Vector3();

      const dis = additionCenter.clone().sub(center);
      const repX = dis.x - dis.x * scalex;
      const repZ = dis.z - dis.z * scaleZ;

      this.mesh.scale.x *= scalex;
      this.mesh.scale.z *= scaleZ;

      this.mesh.translateX(-repX);
      this.mesh.translateZ(-repZ);

      this.mesh.updateMatrixWorld();
      this.scale = new THREE.Vector3(this.scale.x * scalex, this.scale.y, this.scale.z * scaleZ);
      this.meshPosition = this.mesh.position;
      this.isUpdated = true;

    }
  }

  ScaleXWithCenter(scale: number, center: THREE.Vector3) {
    if (!scale || Math.abs(scale - 1) < 0.01) return false;

    if (this.status !== AdditionNewStatus.removed) {
      this.viewMesh.geometry.computeBoundingBox();
      const additionCenter = this.viewMesh.geometry.boundingBox?.getCenter(new THREE.Vector3()) ?? new THREE.Vector3();

      const dis = additionCenter.clone().sub(center);
      const rep = dis.sub(dis.clone().multiplyScalar(scale));

      this.mesh.scale.x *= scale;
      this.mesh.translateX(-rep.x);

      this.mesh.updateMatrixWorld();
      this.scale.x *= scale;
      this.meshPosition = this.mesh.position;
      this.isUpdated = true;
    }
  }

  ScaleX(outputMesh: THREE.Mesh, scaleFactor: number, callUpdate: boolean = true): boolean {
    if (!scaleFactor || Math.abs(scaleFactor - 1) < 0.01) return false;
    this.mesh.scale.x *= scaleFactor;
    this.scale.x = this.mesh.scale.x;
    // console.log("scaleX:", this.mesh.scale.x);
    this.mesh.updateMatrixWorld();
    if (callUpdate) {
      this.UpdateAddition(outputMesh, null, true);
    }
    return true;
  }

  ScaleZ(outputMesh: THREE.Mesh, scaleFactor: number, callUpdate: boolean = true): boolean {
    if (!scaleFactor || Math.abs(scaleFactor - 1) < 0.01) return false;
    this.mesh.scale.z *= scaleFactor;
    this.scale.z = this.mesh.scale.z;
    // console.log("scaleZ:", this.mesh.scale.z);
    this.mesh.updateMatrixWorld();
    if (callUpdate) {
      this.UpdateAddition(outputMesh, null, true);
    }
    return true;
  }

  ApplyAddition(outputMesh: THREE.Mesh, doApplyViewMeshOnOutputMesh: boolean = true) {
    outputMesh.geometry.computeBoundsTree();
    this.mesh.geometry.computeBoundsTree();
    const effectBox = new THREE.Box3().setFromObject(this.mesh);
    const positionAttribute = outputMesh.geometry.getAttribute('position');
    const selectedAttribute = outputMesh.geometry.getAttribute('selected');
    if (!selectedAttribute) return;

    const selectedArray = selectedAttribute.array;
    const raycaster = new THREE.Raycaster();
    const additionBoundingBox = new THREE.Box3().setFromObject(this.mesh);
    const updatedPositions = new Float32Array(positionAttribute.array.length);
    updatedPositions.set(positionAttribute.array);

    for (let i = 0; i < positionAttribute.count; i++) {
      const vertex = new THREE.Vector3();
      vertex.fromBufferAttribute(positionAttribute, i);
      const worldVertex = vertex.clone().applyMatrix4(outputMesh.matrixWorld);
      worldVertex.y = additionBoundingBox.min.y;

      if (!(worldVertex.z > effectBox.min.z && worldVertex.z < effectBox.max.z &&
        worldVertex.x > effectBox.min.x && worldVertex.x < effectBox.max.x)) continue;

      const rayDirection = new THREE.Vector3(0, 100, 0);
      raycaster.set(worldVertex, rayDirection);
      const intersects = raycaster.intersectObject(this.mesh);
      if (intersects.length > 0) {
        let offset = intersects[0].distance + (this.mode === AdditionImode.Top ? this.thickness : 0);
        if (this.isReverse) offset = - (additionBoundingBox.getSize(new THREE.Vector3()).y - offset);
        var NewZ = offset * selectedArray[i] * 1.1;

        if (this.mode == AdditionImode.Top) {
          if (offset > updatedPositions[i * 3 + 1] && selectedArray[i] > 0.7)   // 'selectedArray[i] > 0.7' to avoid wall corruption
          {
            updatedPositions[i * 3 + 1] = NewZ;
          }
        }
        else {
          updatedPositions[i * 3 + 1] += NewZ;
        }

        if (updatedPositions[i * 3 + 1] < MIN_FLOOR_FOR_ADDITIONS && selectedArray[i] > 0)
          updatedPositions[i * 3 + 1] = MIN_FLOOR_FOR_ADDITIONS;
      }
    }

    positionAttribute.array.set(updatedPositions);
    positionAttribute.needsUpdate = true;

    outputMesh.geometry.computeVertexNormals();

    if (doApplyViewMeshOnOutputMesh) {
      // console.log("this.viewMesh.scale", this.viewMesh.scale);
      // if (this.viewMesh.scale.y == 0) {
      //   this.viewMesh.scale.y = 0.0001;
      //   this.viewMesh.updateMatrixWorld();
      // }
      //console.log("this.viewMesh.scale", this.viewMesh.scale);
      this.ApplyViewMeshOnOutputMesh(outputMesh, this.viewMesh);
      //console.log('ApplyAddition ApplyViewMeshOnOutputMesh', this.fileName);
    }

    //this.ApplyViewCurveOnOutputMesh(outputMesh, this.viewCurve);
    outputMesh.geometry.disposeBoundsTree();
    this.mesh.geometry.disposeBoundsTree();

    if (this.jointGeometry === null) {
      //console.log("jointGeometry is null");
      this.findCenterIndexOnOtherMesh(outputMesh);
      this.getJointGeometry(outputMesh);
    }
    else this.UpdateJointGeometry();
  }

  mirror(): void {

    const originalGeo = this.mesh.geometry as THREE.BufferGeometry;
    const mirroredGeo = originalGeo.clone();

    const pos = mirroredGeo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setX(i, -pos.getX(i));
    }

    // Negating X reverses the winding of every triangle, so the addition would
    // come out inside-out. flipFaceNormals restores it and recomputes normals.
    flipFaceNormals(mirroredGeo);

    this.mesh.geometry = mirroredGeo;

    this.meshPosition.x = -this.meshPosition.x;

    this.angle = -this.angle;

    this.mesh.position.copy(this.meshPosition);
    this.mesh.rotation.set(0, this.angle, 0);
    this.mesh.updateMatrixWorld(true);

    this.SetViewMesh();
    this.viewMesh.position.copy(this.meshPosition);
  }

  getCenter(): void {
    const box = new THREE.Box3().setFromObject(this.viewMesh);
    const center = new THREE.Vector3();
    box.getCenter(center);
    this.additionCenter = center;
  }
  getRelativeIndexMesh(designCategory: DesignCategory = DEFAULT_DESIGN_CATEGORY): number | null {
    return ADDITION_TIES_TO_INDEX_NAMES_BY_CATEGORY[designCategory][this.name] ?? null;
  }

}
